@use '@/styles/variables' as *;

// ─── Wrapper ──────────────────────────────────────────────────────────────────

.contact {
  max-width: 600px;
  margin: 6rem auto 0;
  width: 100%;
}

// ─── Form area ────────────────────────────────────────────────────────────────

.form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.fieldRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.label {
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}

.input,
.textarea {
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.7rem 0.9rem;
  font: inherit;
  font-size: 0.925rem;
  color: var(--foreground);
  outline: none;
  transition: border-color 0.2s ease;
  width: 100%;

  &::placeholder {
    color: var(--muted-foreground);
    opacity: 0.55;
  }

  &:focus {
    border-color: var(--forest);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.inputError {
    border-color: var(--destructive);

    &:focus {
      border-color: var(--destructive);
    }
  }
}

.fieldError {
  font-size: 0.75rem;
  color: var(--destructive);
  margin: 0;
}

.textarea {
  resize: vertical;
  min-height: 130px;
  line-height: 1.6;
}

// ─── Submit button ────────────────────────────────────────────────────────────

.submitBtn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.75rem;
  background: var(--forest);
  color: var(--primary-foreground);
  border: none;
  border-radius: var(--radius);
  font: inherit;
  font-size: 0.925rem;
  font-weight: 500;
  cursor: pointer;
  align-self: center;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid var(--foreground);
    outline-offset: 3px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spinner {
  animation: spin 0.75s linear infinite;
  flex-shrink: 0;
}

// ─── Success state ────────────────────────────────────────────────────────────

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0;
  padding: 2.75rem 2rem;
  background: color-mix(in srgb, var(--forest) 8%, var(--background));
  border: 1px solid color-mix(in srgb, var(--forest) 22%, transparent);
  border-radius: var(--radius);
  animation: fadeSlideIn 0.35s ease both;
}

.successIcon {
  color: var(--forest);
  margin-bottom: 1rem;
}

.successTitle {
  font-family: var(--font-display);
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--foreground);
  letter-spacing: -0.01em;
  margin-bottom: 0.45rem;
}

.successBody {
  font-size: 0.9rem;
  color: var(--muted-foreground);
  line-height: 1.65;
  max-width: 34ch;
}

// ─── Direct contact ───────────────────────────────────────────────────────────

.directContact {
  margin-top: 2.5rem;
}

.divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }

  span {
    font-size: 0.72rem;
    font-weight: 500;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--muted-foreground);
    white-space: nowrap;
  }
}

.links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: center;
}

.linkItem {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--muted-foreground);
  text-decoration: none;
  transition:
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    border-color: var(--forest);
    color: var(--forest);
  }

  &:nth-child(2):hover {
    border-color: #0a66c2;
    color: #0a66c2;
  }

  &:nth-child(3):hover {
    border-color: #e1306c;
    color: #e1306c;
  }

  &:nth-child(4):hover {
    border-color: #fc6d26;
    color: #fc6d26;
  }

  &:focus-visible {
    outline: 2px solid var(--foreground);
    outline-offset: 3px;
  }
}

// ─── Responsive ───────────────────────────────────────────────────────────────

@media (max-width: #{$breakpoint-sm}) {
  .fieldRow {
    grid-template-columns: 1fr;
  }
}
